import type { Invoice } from '../types'

const DAY_MS = 86_400_000
const MOSCOW_TIME_ZONE = 'Europe/Moscow'

export const INVOICE_DUE_SOON_DAYS = 7

export type InvoiceTimingStatus =
  | 'issued'
  | 'shipped'
  | 'upcoming'
  | 'due_soon'
  | 'due_today'
  | 'overdue'
  | 'paid'
  | 'cancelled'

export type InvoiceTimingTone =
  | 'neutral'
  | 'info'
  | 'warning'
  | 'danger'
  | 'success'
  | 'muted'

export interface InvoiceTiming {
  status: InvoiceTimingStatus
  tone: InvoiceTimingTone
  /**
   * Calendar days until the payment deadline. A negative value is the number
   * of overdue days with a minus sign. It is null until the invoice is shipped,
   * and for closed invoices.
   */
  days: number | null
  dueOn: string | null
  label: string
}

interface LocalDateParts {
  year: number
  month: number
  day: number
}

function parseDateOnly(value: string): LocalDateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const candidate = new Date(year, month - 1, day)

  if (
    candidate.getFullYear() !== year
    || candidate.getMonth() !== month - 1
    || candidate.getDate() !== day
  ) {
    return null
  }

  return { year, month, day }
}

function localDateParts(value: Date | string): LocalDateParts | null {
  if (typeof value === 'string') {
    const dateOnly = parseDateOnly(value)
    if (dateOnly) return dateOnly

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null
    return moscowDateParts(parsed)
  }

  if (Number.isNaN(value.getTime())) return null
  return moscowDateParts(value)
}

function moscowDateParts(value: Date): LocalDateParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MOSCOW_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value)
  return { year: part('year'), month: part('month'), day: part('day') }
}

function dateOnlyKey(parts: LocalDateParts): string {
  return [
    String(parts.year).padStart(4, '0'),
    String(parts.month).padStart(2, '0'),
    String(parts.day).padStart(2, '0'),
  ].join('-')
}

export function todayInMoscow(value = new Date()): string {
  return dateOnlyKey(moscowDateParts(value))
}

function addCalendarDays(parts: LocalDateParts, days: number): LocalDateParts {
  const result = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days))
  return {
    year: result.getUTCFullYear(),
    month: result.getUTCMonth() + 1,
    day: result.getUTCDate(),
  }
}

function calendarDaysBetween(from: LocalDateParts, to: LocalDateParts): number {
  const fromUtc = Date.UTC(from.year, from.month - 1, from.day)
  const toUtc = Date.UTC(to.year, to.month - 1, to.day)
  return Math.round((toUtc - fromUtc) / DAY_MS)
}

export function dueOn(invoice: Invoice): string | null {
  if (!invoice.shippedOn) return null

  const shipped = parseDateOnly(invoice.shippedOn)
  if (!shipped) return null

  const terms = invoice.paymentTermsDays
  if (!Number.isInteger(terms) || terms < 0) return null

  return dateOnlyKey(addCalendarDays(shipped, terms))
}

export function invoiceTiming(
  invoice: Invoice,
  today: Date | string = new Date(),
): InvoiceTiming {
  const deadline = dueOn(invoice)

  if (invoice.status === 'paid') {
    return { status: 'paid', tone: 'success', days: null, dueOn: deadline, label: 'Оплачено' }
  }

  if (invoice.status === 'cancelled') {
    return { status: 'cancelled', tone: 'muted', days: null, dueOn: deadline, label: 'Отменено' }
  }

  if (invoice.status === 'issued') {
    return {
      status: 'issued',
      tone: invoice.paymentTermsDays === 0 ? 'warning' : 'neutral',
      days: null,
      dueOn: null,
      label: invoice.paymentTermsDays === 0 ? 'Предоплата' : 'Отсрочка не началась',
    }
  }

  const current = localDateParts(today)
  const due = deadline ? parseDateOnly(deadline) : null
  if (!current || !due) {
    return { status: 'shipped', tone: 'neutral', days: null, dueOn: deadline, label: 'Укажите дату отгрузки' }
  }

  const days = calendarDaysBetween(current, due)
  if (days < 0) {
    const count = Math.abs(days)
    return {
      status: 'overdue',
      tone: 'danger',
      days,
      dueOn: deadline,
      label: invoice.paymentTermsDays === 0
        ? `Предоплата просрочена на ${count} дн.`
        : `Просрочено на ${count} дн.`,
    }
  }

  if (days === 0) {
    return {
      status: 'due_today',
      tone: 'warning',
      days,
      dueOn: deadline,
      label: invoice.paymentTermsDays === 0 ? 'Ожидается предоплата' : 'Срок оплаты сегодня',
    }
  }

  if (days <= INVOICE_DUE_SOON_DAYS) {
    return { status: 'due_soon', tone: 'warning', days, dueOn: deadline, label: `До просрочки ${days} дн.` }
  }

  return { status: 'upcoming', tone: 'info', days, dueOn: deadline, label: `До оплаты ${days} дн.` }
}

const URGENCY: Record<InvoiceTimingStatus, number> = {
  overdue: 0,
  due_soon: 1,
  due_today: 1,
  upcoming: 2,
  shipped: 3,
  issued: 4,
  paid: 5,
  cancelled: 6,
}

export function sortInvoicesByUrgency(
  invoices: readonly Invoice[],
  today: Date | string = new Date(),
): Invoice[] {
  return invoices
    .map((invoice, index) => ({ invoice, index, timing: invoiceTiming(invoice, today) }))
    .sort((left, right) => {
      const statusDifference = URGENCY[left.timing.status] - URGENCY[right.timing.status]
      if (statusDifference) return statusDifference

      if (left.timing.days !== null && right.timing.days !== null) {
        const daysDifference = left.timing.days - right.timing.days
        if (daysDifference) return daysDifference
      }

      const issuedDifference = left.invoice.issuedOn.localeCompare(right.invoice.issuedOn)
      if (issuedDifference) return issuedDifference

      return left.index - right.index
    })
    .map(({ invoice }) => invoice)
}
